import { LegalEntityFormFieldNames } from 'features/new-actor/enums/legalEntityFormFieldNames';
import { PersonalInformationFieldNames } from 'features/new-actor/enums/personalInformationFieldNames';
import { emptyFinancialsFormValues } from 'features/new-actor/mappers/optim/mapSubsidiaries';
import { LegalEntityFormValues } from 'features/new-actor/types/optim/legalEntityFormValues';
import { ValidationRules } from 'helpers/createFormConfig';
import { FormConfig } from 'types/form/formConfig';
import * as Yup from 'yup';
import { addressValidationRules } from '../addressValidationRules';
import { baseLegalEntityValidationRules } from '../baseLegalEntityValidationRules';
import { legalEntityContactValidationRules } from './legalEntityContactValidationRules';
import { legalEntityFinancialsValidationRules } from './legalEntityFinancialsValidationRules';

// Financials are validated only when the block is enabled (withFinancialsValidation).
/** Shown on the contact card itself: unlike a missing field, the card cannot be fixed by "completing" it. */
export const DUPLICATE_CONTACT_EMAIL_MESSAGE = 'Cet email est déjà utilisé par un autre contact';

export const legalEntityValidationRules: ValidationRules<
  Exclude<keyof LegalEntityFormValues, 'id' | LegalEntityFormFieldNames.Financials>
> =
{
  ...addressValidationRules,
  ...baseLegalEntityValidationRules,
  [LegalEntityFormFieldNames.CompanyName]: Yup.string()
    .trim()
    .required('Ce champ est requis'),

  [PersonalInformationFieldNames.Phone]: Yup.string()
    .matches(/^(\+33|0)[1-9](\d{2}){4}$/, 'Numéro de téléphone invalide')
    .required('Ce champ est requis'),

  [PersonalInformationFieldNames.Email]: Yup.string()
    .email("L'adresse email n'est pas valide")
    .required('Ce champ est requis'),

  [LegalEntityFormFieldNames.Contacts]: Yup.array()
    .of(
      Yup.object().shape({
        ...legalEntityContactValidationRules,
      })
    )
    .test(
      'has-gerant',
      'Au moins un contact doit avoir le rôle Gérant',
      (contacts) => contacts?.some(contact => contact?.companyRole === 'GERANT') ?? false
    )
    // Miroir de la règle back (CreateOrUpdateAssurePersonneMoraleCommand) : deux
    // contacts ne peuvent pas partager le même email. Erreur posée sur le contact
    // en doublon pour allumer sa carte, plutôt qu'un rejet 400 tardif.
    .test('emails-uniques', function (contacts) {
      if (!contacts) return true;
      const seen = new Set<string>();
      const errors: Yup.ValidationError[] = [];
      contacts.forEach((contact: any, index: number) => {
        const email = contact?.email?.trim().toLowerCase();
        if (!email) return;
        if (seen.has(email)) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].email`,
              message: DUPLICATE_CONTACT_EMAIL_MESSAGE
            })
          );
        } else {
          seen.add(email);
        }
      });
      return errors.length ? new Yup.ValidationError(errors) : true;
    })
}

/** Rules of the accounting data alone (legalEntityOptions.financials), shared by the form schema and the sync guard. */
export const financialsBlockSchemaFields = {
  [LegalEntityFormFieldNames.Financials]: Yup.object(legalEntityFinancialsValidationRules).required()
};

/**
 * Legal entity form config with the accounting data block enabled (legalEntityOptions.financials):
 * they become required. The default config is left untouched.
 */
export function withFinancialsValidation<T extends string>(formConfig: FormConfig<T>): FormConfig<T> {
  const financials = LegalEntityFormFieldNames.Financials;
  const initialValues = formConfig.initialValues as Record<string, any>;
  // Loosely typed on purpose: extending the generic Record<T, …> schema is too complex for tsc.
  const validationSchema = formConfig.validationSchema as unknown as Yup.AnyObjectSchema;

  return {
    initialValues: {
      ...initialValues,
      [financials]: initialValues[financials] ?? emptyFinancialsFormValues()
    } as Record<T, any>,
    validationSchema: validationSchema.shape(financialsBlockSchemaFields) as unknown as FormConfig<T>['validationSchema']
  };
}
